// Parsing CHANGELOG.md and comparing what it advertises against what is
// actually running.
//
// Split out of ChangelogModal so the version reconciliation — the part that
// decides "you are on 0.89.3, 0.90.0 is out" — can be tested without mounting a
// Radix dialog or stubbing fetch.
//
// The two figures come from different places on purpose and WILL disagree:
// `packageInfo.version` is compiled into the bundle, while the changelog is
// fetched live. Since the service worker deliberately holds the old shell until
// the user takes the update (see service-worker.js), a freshly deployed release
// is readable in the changelog for as long as the user keeps declining it.
// Reporting one number for both is what made the menu look stale; reporting
// both, and offering the update, is the fix.

export function normalizeVersion(version) {
  return String(version || "")
    .trim()
    .replace(/^v/i, "")
    .replace(/^\[(.*)\]$/, "$1");
}

/** Negative when `a` is newer. Sorting with it yields newest-first. */
export function compareSemver(a, b) {
  const pa = String(a).split(".").map(Number);
  const pb = String(b).split(".").map(Number);
  for (let i = 0; i < 3; i += 1) {
    const diff = (pb[i] || 0) - (pa[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// A heading like "## [0.90.0] - 2026-09-06". The version may also be a word
// ("Unreleased"), which carries no ordering and must never be offered as an
// update.
const RELEASE_HEADING = /^## \[(.+?)\](?:\s*-\s*(.+))?$/;

const SEMVER = /^\d+\.\d+\.\d+$/;

export const isSemver = (version) => SEMVER.test(normalizeVersion(version));

export function parseChangelog(raw) {
  const releases = [];
  const lines = String(raw || "").split(/\r?\n/);
  let currentRelease = null;
  let sectionStart = 0;

  for (let index = 0; index < lines.length; index += 1) {
    const headingMatch = lines[index].match(RELEASE_HEADING);
    if (!headingMatch) continue;

    if (currentRelease) {
      currentRelease.content = lines
        .slice(sectionStart, index)
        .join("\n")
        .trim();
    }

    currentRelease = {
      version: headingMatch[1],
      date: headingMatch[2] || "Pending release",
      content: "",
    };
    releases.push(currentRelease);
    sectionStart = index + 1;
  }

  if (currentRelease) {
    currentRelease.content = lines.slice(sectionStart).join("\n").trim();
  }

  releases.sort((a, b) => compareSemver(a.version, b.version));
  return releases;
}

/**
 * The newest real release in the changelog, or null.
 *
 * "Unreleased" is skipped: it is a staging area, not something a user can be
 * running or updating to.
 */
export function latestReleaseVersion(releases) {
  if (!Array.isArray(releases)) return null;
  for (const release of releases) {
    const version = normalizeVersion(release?.version);
    if (isSemver(version)) return version;
  }
  return null;
}

/**
 * What the changelog should say about the build in the browser right now.
 *
 * `updateAvailable` means the changelog advertises a release newer than the
 * bundle that is running — which is precisely the state the old UI rendered as
 * a stale "Current" pill halfway down the list with no explanation.
 */
export function resolveVersionState(releases, runningVersion) {
  const running = normalizeVersion(runningVersion);
  const latest = latestReleaseVersion(releases);
  const comparable = isSemver(running) && !!latest;

  return {
    running,
    latest,
    updateAvailable: comparable ? compareSemver(latest, running) < 0 : false,
    // The entry to open when the modal appears: whatever is newest, so opening
    // the changelog shows what is new rather than what you have already read.
    expandVersion: latest || releases?.[0]?.version || null,
  };
}

/** Is this release newer than the running build (i.e. not yet taken)? */
export function isUnreleasedForUser(version, runningVersion) {
  const v = normalizeVersion(version);
  const running = normalizeVersion(runningVersion);
  if (!isSemver(v) || !isSemver(running)) return false;
  return compareSemver(v, running) < 0;
}
